import { Box, Button, ButtonProps, Stack, Tooltip, Typography } from "@mui/material";
import { StatCard } from "components/StatCard";
import { StatusChip } from "components/StatusChip";
import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import {
  InvoiceAuditIssueFragment,
  InvoiceAuditSummaryFragment,
  WasteInvoiceChargeAuditResult,
} from "generated-graphql/graphql";
import { ReactNode } from "react";

import { StatCardGrid } from "../../components/StatCardGrid";
import {
  InvoiceTrackingSummaryCard,
  InvoiceTrackingSummarySkeleton,
} from "../InvoiceTrackingSummaryCard";
import { formatContractRateField, getContractRateFields } from "./contractRateFields";
import {
  AUDIT_RESULT_LABELS,
  formatIssueLabel,
  formatAuditMoney,
  formatAuditTotal,
} from "./formatAuditValues";

export function UnavailableButton({ children, ...props }: ButtonProps) {
  return (
    <Tooltip title="Not available yet">
      <span>
        <Button {...props} disabled>
          {children}
        </Button>
      </span>
    </Tooltip>
  );
}

export function AuditResult({ result }: { result: WasteInvoiceChargeAuditResult }) {
  return (
    <StatusChip
      label={AUDIT_RESULT_LABELS[result]}
      title={AUDIT_RESULT_LABELS[result]}
      color={result === "RATE_MISMATCH" ? "error" : result === "MATCH" ? "success" : "warning"}
      sx={{ maxWidth: "100%" }}
    />
  );
}

export function AuditFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {children ?? "—"}
      </Typography>
    </Box>
  );
}

export function AuditSummary({
  summary,
  detail = false,
}: {
  summary?: InvoiceAuditSummaryFragment;
  detail?: boolean;
}) {
  if (!summary) return <InvoiceTrackingSummarySkeleton count={5} />;
  const overages = formatAuditTotal(summary.calculatedLineCount, summary.totalOverages);
  const finding = (count: number) => (count > 0 ? "warning.dark" : "text.primary");
  const overageAmount =
    summary.calculatedLineCount > 0 ? (safeParseFloat(String(summary.totalOverages ?? 0)) ?? 0) : 0;
  const metrics = detail
    ? [
        {
          label: "Total billed amount",
          value: formatAuditMoney("billedAmount", summary.billedAmount),
          unit: "Invoiced",
        },
        {
          label: "Total overages",
          value: overages,
          unit: "Over contract",
          color: finding(overageAmount),
        },
        { label: "Line items", value: summary.lineCount.toLocaleString(), unit: "Lines" },
        {
          label: "Rate mismatch lines",
          value: summary.nonCompliantLineCount.toLocaleString(),
          unit: "Lines",
          color: finding(summary.nonCompliantLineCount),
        },
      ]
    : [
        {
          label: "Total billed amount",
          value: formatAuditMoney("billedAmount", summary.billedAmount),
          unit: "Invoiced",
        },
        {
          label: "Total overages",
          value: overages,
          unit: "Over contract",
          color: finding(overageAmount),
        },
        { label: "Total invoice lines", value: summary.lineCount.toLocaleString(), unit: "Lines" },
        {
          label: "Total rate mismatch lines",
          value: summary.nonCompliantLineCount.toLocaleString(),
          unit: "Lines",
          color: finding(summary.nonCompliantLineCount),
        },
      ];

  metrics.push({
    label: "Unresolved lines",
    value: summary.unresolvedLineCount.toLocaleString(),
    unit: "Lines",
    color: finding(summary.unresolvedLineCount),
  });
  return (
    <InvoiceTrackingSummaryCard>
      <StatCardGrid testId="audit-summary-cards">
        {metrics.map(({ label, value, unit, color }) => (
          <StatCard
            key={label}
            caption={label}
            value={value}
            subCaption={unit}
            valueColor={color}
            captionPlacement="top"
          />
        ))}
      </StatCardGrid>
    </InvoiceTrackingSummaryCard>
  );
}

function AuditRateReferences({ issue }: { issue: InvoiceAuditIssueFragment }) {
  if (issue.referencedRates.length === 0) {
    return issue.rateCardEntryIds.length > 0 ? (
      <Typography variant="caption" display="block" sx={{ overflowWrap: "anywhere" }}>
        Contract rate references: {issue.rateCardEntryIds.join(", ")}
      </Typography>
    ) : null;
  }

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {issue.code === "AMBIGUOUS_RATE"
          ? "Candidate contract entries"
          : "Referenced contract entries"}
      </Typography>
      {issue.referencedRates.map((rate) => (
        <Box key={rate.id}>
          <Typography variant="body2" fontWeight={600}>
            {rate.description}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 1,
              mt: 1,
            }}
          >
            {getContractRateFields(rate).flatMap((field) =>
              field.kind === "rule" ||
              field.kind === "money" ||
              field.kind === "unitRate" ||
              field.kind === "quantity"
                ? [
                    <AuditFact key={field.key} label={field.label}>
                      {formatContractRateField(field)}
                    </AuditFact>,
                  ]
                : []
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            Rate ID: {rate.id}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

export function AuditIssues({ issues }: { issues: readonly InvoiceAuditIssueFragment[] }) {
  return (
    <Stack spacing={1}>
      {issues.map((issue) => (
        <Box key={issue.key}>
          <Typography variant="body2" fontWeight={600}>
            {formatIssueLabel(issue.code)}
          </Typography>
          <Typography variant="body2">{issue.message}</Typography>
          <Typography variant="caption" color="text.secondary">
            {issue.blocksRateCalculation
              ? "Prevents rate calculation"
              : "Does not prevent a complete pre-fee calculation"}
          </Typography>
          <AuditRateReferences issue={issue} />
        </Box>
      ))}
    </Stack>
  );
}
